import {useEffect} from "react";
import {observer} from "mobx-react";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import HeaderMenu, {HeaderMenuOption} from "../header-menu/header-menu";
import Icon from "../../../../icons/icon/icon";
import FontBody1 from "../../../../fonts/font-body1/font-body1";
import IconAccount from "./icons/icon-account";
import IconProfile from "./icons/icon-profile";
import {FONT_COLOR, FONT_VARIANT} from "../../../../fonts/font-props";
import {NavLink, useNavigate} from "react-router-dom";
import IconCard from "./icons/icon-card";
import IconPlan from "./icons/icon-plan";
import IconStore from "./icons/icon-store";
import IconLogout from "./icons/icon-logout";
import pageUrlCoordinationStore from "../../page-url-broadcaster/page-url-coordination-store";
import {APP_PATHS, AppPath} from "../../../../../constants/app-paths";
import currentAccountStore from "../../../../../pages/accounts/stores/current-account-store";
import currentPortalStore from "../../../../../stores/current-portal-store";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";

import "./personal-area-menu.scss";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { isActiveClassName } from "../../app-sidebar/app-sidebar-menu-items/menu-links/menu-items";


const DASHBOARD_TOP_BAR_SCOPE = 'dashboard-top-bar';
const DASHBOARD_SIDE_MENU_SCOPE = 'dashboard-side-menu';

const PersonalAreaMenu = observer(() => {
    const navigate = useNavigate();
    const {getTranslation} = useDashboardTranslation();

    useEffect(() => {
        currentPortalStore.load();
    }, []);

    const handleMenuLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, to: AppPath): void => {
        /** 
         * Define if the link in not going to be opened in the same tab and do nothing - 
        * let a natural browser behaviour happen.
        * The key markers are: not any additional key was pressed, left mouse button was
        * clicked.
        */
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
            return;
        }

        /** Navigate using react router */
        e.preventDefault();
        navigate(to);
    };

    const getFontVariant = (path: string): FONT_VARIANT | undefined => {
        const laravelAbsoluteUrl = pageUrlCoordinationStore.constructLaravelAbsoluteUrl(path);
        if (isActiveClassName(laravelAbsoluteUrl)) {
            return FONT_VARIANT.BOLD;
        }
        return undefined;
    }

    return (
        <HeaderMenu 
            toggleContents={<Icon Component={IconAccount} />} 
            className="personal-area-menu__toggle"
            onToggle={() => rmsMixpanelStore.trackEvent('d&_header_account')}
        >
            <div className="personal-area-menu">
                <div className="personal-area-menu__user-info" onClick={(e) => e.stopPropagation()}>
                    <Icon Component={IconAccount} color="var(--color-primary-2)"/>
                    <div>
                        <div>
                            <FontBody1 color={FONT_COLOR.PRIMARY2}>{currentAccountStore.account?.displayEmail}</FontBody1>
                        </div>
                        <div>
                            <FontBody1 color={FONT_COLOR.SEMANTIC_3}>{currentPortalStore.currentPortal?.displayName || currentPortalStore.currentPortal?.name}</FontBody1>
                        </div>
                    </div>
                </div>
                <HeaderMenuOption onClick={() => rmsMixpanelStore.trackEvent('d&_header_account_profile')}>
                    <a 
                        href={pageUrlCoordinationStore.constructLaravelAbsoluteUrl(APP_PATHS.PROFILE)} 
                        onClick={(e) => handleMenuLinkClick(e, APP_PATHS.PROFILE)} 
                        className="personal-area-menu__link"
                    >
                        <Icon Component={IconProfile} />
                        <FontBody1 color={FONT_COLOR.PRIMARY2} variant={getFontVariant(APP_PATHS.PROFILE)}>
                            {getTranslation('MY_PROFILE', DASHBOARD_TOP_BAR_SCOPE)}
                        </FontBody1>
                    </a>
                </HeaderMenuOption>
                {
                    dashboardSettingsStore.isPathAvailable(APP_PATHS.BILLING) && (
                        <HeaderMenuOption onClick={() => rmsMixpanelStore.trackEvent('d&_header_account_billing')} >
                            <a
                                href={pageUrlCoordinationStore.constructLaravelAbsoluteUrl(APP_PATHS.BILLING)}
                                onClick={(e) => handleMenuLinkClick(e, APP_PATHS.BILLING)}
                                className="personal-area-menu__link"
                            >
                                <Icon Component={IconCard} />
                                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={getFontVariant(APP_PATHS.BILLING)}>
                                    {getTranslation('BILLING', DASHBOARD_SIDE_MENU_SCOPE)}
                                </FontBody1>
                            </a>
                        </HeaderMenuOption>
                    )
                }
                {
                    dashboardSettingsStore.isPathAvailable(APP_PATHS.SUBSCRIPTION_PLANS) && (
                        <HeaderMenuOption onClick={() => rmsMixpanelStore.trackEvent('d&_header_account_plans')}>
                            <a
                                href={pageUrlCoordinationStore.constructLaravelAbsoluteUrl(APP_PATHS.SUBSCRIPTION_PLANS)}
                                onClick={(e) => handleMenuLinkClick(e, APP_PATHS.SUBSCRIPTION_PLANS)}
                                className="personal-area-menu__link"
                            >
                                <Icon Component={IconPlan} />
                                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={getFontVariant(APP_PATHS.SUBSCRIPTION_PLANS)}>
                                    {getTranslation('SUBSCRIPTION_PLANS', DASHBOARD_TOP_BAR_SCOPE)}
                                </FontBody1>
                            </a>
                        </HeaderMenuOption>
                    )
                }
                {
                    dashboardSettingsStore.isPathAvailable(APP_PATHS.ADMIN_ACCOUNTS) && (
                        <HeaderMenuOption onClick={() => rmsMixpanelStore.trackEvent('d&_header_account_store_account')}>
                            <a
                                href={pageUrlCoordinationStore.constructLaravelAbsoluteUrl(APP_PATHS.ADMIN_ACCOUNTS)}
                                onClick={(e) => handleMenuLinkClick(e, APP_PATHS.ADMIN_ACCOUNTS)}
                                className="personal-area-menu__link"
                            >
                                <Icon Component={IconStore} />
                                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={getFontVariant(APP_PATHS.ADMIN_ACCOUNTS)}>
                                    {getTranslation('STORE_ACCOUNT', DASHBOARD_TOP_BAR_SCOPE)}
                                </FontBody1>
                            </a>
                        </HeaderMenuOption>
                    )
                }
                <HeaderMenuOption onClick={() => rmsMixpanelStore.trackEvent('d&_header_account_logout')}>
                    <NavLink to="" className="personal-area-menu__link" onClick={(e) => {
                        e.preventDefault();
                        currentAccountStore.logout();
                    }}>
                        <Icon Component={IconLogout} />
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>
                            {getTranslation('LOGOUT', DASHBOARD_TOP_BAR_SCOPE)}
                        </FontBody1>
                    </NavLink>
                </HeaderMenuOption>
            </div>
        </HeaderMenu>
    )
});

export default PersonalAreaMenu;